// Privacy policy v1.0 (effective 1 September 2026), served until v1.1 takes effect on 1 November 2026
// (src/content/legal/index.ts getGdprDoc). Delete this folder after that date.
import type { LegalDoc } from "../types";

// Portuguese (RGPD). Tradução da política de referência (v1.0, 1 set. 2026).
const gdprPt: LegalDoc = {
  title: "Política de Privacidade e Proteção de Dados (GDPR / RGPD)",
  effectiveDate: "1 de setembro de 2026",
  version: "1.0",
  intro:
    "Esta política explica como a Photohawk trata os dados pessoais ao abrigo do Regulamento Geral sobre a Proteção de Dados da UE (GDPR) e do seu equivalente francês, o RGPD (Règlement Général sur la Protection des Données), bem como do UK GDPR. O RGPD e o GDPR designam a mesma legislação.",
  sections: [
    {
      heading: "1. Quem somos",
      blocks: [
        {
          type: "p",
          text: "A Photohawk é uma plataforma de fotografia de eventos que permite a fotógrafos e organizadores publicar, pesquisar e vender fotos de eventos.",
        },
        {
          type: "ul",
          items: [
            "Entidade jurídica: CLOUDCONCUR LTD (número de sociedade 13399913), uma sociedade registada em Inglaterra e no País de Gales, que opera sob o nome comercial Photohawk.",
            "Endereço da sede: 23 Caldey Gardens, Ingleby Barwick, Stockton-on-Tees, TS17 5HW, United Kingdom.",
            "Encarregado da Proteção de Dados (DPO): Andrew Hammond — contacto support@photohawk.com.",
            "Representante na UE (art. 27 do GDPR): não designámos um representante na UE, com fundamento em que o nosso tratamento de dados de residentes da UE é ocasional e não constitui a nossa atividade principal (art. 27(2)). Reavaliamos regularmente este ponto e designaremos um representante se o nosso tratamento se alterar.",
            "Autoridades de controlo: o Information Commissioner's Office (ICO) para o Reino Unido, e as autoridades da UE, incluindo a CNIL em França, para as pessoas situadas na UE.",
          ],
        },
      ],
    },
    {
      heading: "2. Os nossos dois papéis — responsável pelo tratamento e subcontratante",
      blocks: [
        {
          type: "p",
          text: "A Photohawk dirige-se a duas categorias de pessoas, e o nosso papel jurídico difere para cada uma:",
        },
        {
          type: "ul",
          items: [
            "Fotógrafos e organizadores (os nossos clientes). Relativamente aos dados da sua conta (nome, e-mail, dados profissionais, credenciais de acesso, faturação e pagamentos), a Photohawk é o responsável pelo tratamento.",
            "Participantes em eventos e compradores (os seus clientes). Relativamente aos dados das pessoas que figuram nas fotos e que as compram, o utilizador (o fotógrafo/organizador) é o responsável pelo tratamento e a Photohawk é o seu subcontratante — tratamos esses dados de acordo com as suas instruções para gerir as galerias, a pesquisa e as vendas que configurou. Um acordo de tratamento de dados (ver §11) rege esta relação.",
          ],
        },
      ],
    },
    {
      heading: "3. Que dados pessoais tratamos",
      blocks: [
        { type: "p", text: "Para fotógrafos/organizadores (somos o responsável pelo tratamento):" },
        {
          type: "ul",
          items: [
            "Identidade e contacto: nome, e-mail, telefone, denominação comercial, endereço.",
            "Conta e segurança: credenciais de acesso (via AWS), sessões.",
            "Dados financeiros: dados de subscrição/faturação (via Stripe) e dados de pagamento (via Trolley).",
          ],
        },
        { type: "p", text: "Para participantes/compradores (somos subcontratante, por conta do organizador):" },
        {
          type: "ul",
          items: [
            "Identidade e contacto: nome, e-mail, número de telefone, endereço postal/de faturação.",
            "Fotografias dos participantes.",
            "Dados biométricos — quando a pesquisa por rosto/selfie está ativada (ver §4).",
            "Dados de encomenda e de pagamento (tratados através dos nossos prestadores de pagamento; não conservamos os números de cartão completos).",
            "Estado de consentimento de marketing, quando o participante o autoriza.",
          ],
        },
      ],
    },
    {
      heading: "4. Pesquisa por rosto e selfie (dados biométricos de categoria especial)",
      blocks: [
        {
          type: "p",
          text: "Quando um organizador ativa a pesquisa por selfie/rosto, a Photohawk cria uma representação matemática dos rostos para que um participante possa encontrar as suas próprias fotos.",
        },
        {
          type: "ul",
          items: [
            "Finalidade: exclusivamente para associar uma pessoa às suas próprias fotos do evento.",
            "Local: tratados e armazenados na União Europeia (AWS, região da Irlanda).",
            "Conservação: os dados biométricos/de rosto são automaticamente eliminados quando uma galeria expira (no prazo de ~60 dias) e aquando da eliminação de uma galeria — consoante o que ocorrer primeiro.",
            "Fundamento jurídico (art. 9 do GDPR): os dados biométricos constituem uma categoria especial e recebem proteção adicional: são utilizados apenas para que as pessoas possam encontrar as suas próprias fotos nessa galeria. Enquanto responsável pelo tratamento dos participantes, o administrador da galeria decide se a pesquisa por rosto é adequada ao seu evento e permitida no local onde decorre. Quando a lei local exige o consentimento das pessoas fotografadas, o administrador tem de o ter, por exemplo através da inscrição no evento; caso contrário, deve utilizar em vez disso a pesquisa por dorsal e por hora, que não utiliza dados faciais. A Photohawk disponibiliza um passo de consentimento integrado: o participante é convidado a consentir antes de a sua selfie ser utilizada para a pesquisa por rosto, podendo recusar.",
            "Menores: ver §13.",
          ],
        },
      ],
    },
    {
      heading: "5. Por que utilizamos os dados e os nossos fundamentos jurídicos",
      blocks: [
        {
          type: "ul",
          items: [
            "Para prestar o serviço (alojar galerias, executar a pesquisa, processar encomendas): execução de um contrato.",
            "Pagamentos, transferências, registos fiscais e contabilísticos: execução de um contrato e cumprimento de uma obrigação legal.",
            "Pesquisa por rosto/selfie (art. 9): o consentimento explícito da pessoa que pesquisa, antes de a sua selfie ser utilizada; para as pessoas fotografadas, conforme descrito no §4.",
            "E-mails/mensagens de marketing: apenas com o consentimento do destinatário (opt-in), que pode ser retirado a qualquer momento.",
            "Cookies de análise de audiência e publicitários: apenas com o seu consentimento, dado através do nosso banner de cookies (ver §12).",
            "Segurança, prevenção da fraude e melhoria do serviço: o nosso interesse legítimo, ponderado face aos seus direitos.",
          ],
        },
      ],
    },
    {
      heading: "6. Onde os seus dados são armazenados e transferências internacionais",
      blocks: [
        {
          type: "ul",
          items: [
            "Localização dos dados: os dados dos participantes e das contas — incluindo as fotos e os dados biométricos de rosto — são alojados na União Europeia (AWS, Irlanda / eu-west-1). O nosso serviço de identidade (AWS) está igualmente na UE.",
            "Transferências internacionais: alguns dos nossos subcontratantes (ver §7) operam fora da UE/do Reino Unido (por ex. nos Estados Unidos). Quando os dados pessoais são transferidos para fora da UE/do Reino Unido, baseamo-nos numa decisão de adequação quando aplicável e, na sua falta, nas Cláusulas Contratuais-Tipo (CCT) da Comissão Europeia, juntamente com o Adendo de Transferência de Dados Internacionais do Reino Unido, acrescidas de garantias adicionais quando necessário.",
          ],
        },
      ],
    },
    {
      heading: "7. Subcontratantes",
      blocks: [
        {
          type: "p",
          text: "Recorremos a um pequeno número de prestadores cuidadosamente selecionados para prestar o serviço, cada um vinculado por um acordo de tratamento de dados (e pelas CCT/Adendo do Reino Unido quando tratam dados fora da UE/do Reino Unido):",
        },
        {
          type: "table",
          head: ["Prestador", "Finalidade", "Dados envolvidos"],
          rows: [
            ["Amazon Web Services (AWS)", "Alojamento, armazenamento, identidade, pesquisa por rosto, e-mail transacional — região da UE", "Todos os dados pessoais, fotos, dados biométricos"],
            ["Cloudflare (incl. R2)", "CDN, domínios personalizados, armazenamento de objetos", "Fotos, dados de pedido"],
            ["Stripe", "Pagamentos por cartão e subscrições", "Dados de pagamento de compradores e clientes"],
            ["Xendit", "Pagamentos (Sudeste Asiático)", "Dados de pagamento dos compradores"],
            ["Paystack", "Pagamentos (África)", "Dados de pagamento dos compradores"],
            ["Trolley", "Pagamentos aos fotógrafos", "Identidade e dados bancários do beneficiário"],
            ["Postmark", "E-mail transacional", "Nomes, endereços de e-mail"],
            ["Twilio / WhatsApp", "Entrega de fotos e notificações", "Nomes, números de telefone"],
            ["Google (Google Analytics)", "Análise de utilização do site (sujeita a consentimento)", "Dados de utilização/dispositivo, identificadores"],
            ["Meta Platforms (Meta Pixel)", "Publicidade e medição de conversão (sujeita a consentimento)", "Dados de utilização, identificadores"],
          ],
        },
        { type: "p", text: "Uma lista atualizada está disponível mediante pedido." },
      ],
    },
    {
      heading: "8. Durante quanto tempo conservamos os dados (conservação)",
      blocks: [
        {
          type: "ul",
          items: [
            "Dados biométricos / de rosto: eliminados no prazo de ~60 dias após a expiração de uma galeria, e aquando da eliminação de uma galeria (ver §4).",
            "Fotos e vídeos: a conservação é controlada pelo titular da conta (o organizador/fotógrafo). O conteúdo é conservado enquanto o titular da conta o mantiver; os titulares de conta podem eliminar fotos, vídeos, galerias e registos de participantes a qualquer momento, e eliminamo-los quando o fazem. A Photohawk não impõe um período de vida fixo a este conteúdo.",
            "Encomendas e registos financeiros: conservados durante 7 anos para cumprir as obrigações fiscais e contabilísticas.",
            "Registos de contacto de participantes/compradores: controlados pelo organizador — conservados enquanto o organizador os mantiver, e eliminados quando o organizador os elimina ou mediante um pedido de apagamento verificado.",
            "Listas de marketing: conservadas até à retirada do consentimento.",
            "Os registos operacionais/de segurança são conservados durante 30 dias.",
          ],
        },
      ],
    },
    {
      heading: "9. Como protegemos os dados",
      blocks: [
        {
          type: "ul",
          items: [
            "Em trânsito: cifrados através de HTTPS/TLS em todo o lado.",
            "Em repouso: as fotos e os conteúdos são armazenados na AWS e na Cloudflare R2, que cifram os dados em repouso por predefinição; as credenciais sensíveis (por ex. tokens de contas associadas) são adicionalmente cifradas com AWS.",
            "Controlos de acesso: acesso autenticado via AWS; o acesso do pessoal está limitado ao que é necessário para operar o serviço.",
          ],
        },
      ],
    },
    {
      heading: "10. Os seus direitos",
      blocks: [
        {
          type: "p",
          text: "Ao abrigo do GDPR/RGPD e do UK GDPR, tem o direito de: aceder aos seus dados; retificá-los; apagá-los («direito a ser esquecido»); limitar ou opor-se ao tratamento; à portabilidade dos dados; e de retirar o consentimento a qualquer momento. Tem igualmente o direito de apresentar reclamação junto de uma autoridade de controlo (o ICO no Reino Unido, ou a sua autoridade local, tal como a CNIL em França).",
        },
        { type: "p", text: "Como exercê-los:" },
        {
          type: "ul",
          items: [
            "Fotógrafos/organizadores (somos o responsável pelo tratamento): contacte o nosso DPO em support@photohawk.com. Respondemos no prazo de um mês.",
            "Participantes/compradores (o organizador é o responsável pelo tratamento): contacte o fotógrafo ou o organizador que geriu o seu evento. Os fotógrafos podem eliminar os dados pessoais de um participante — incluindo as suas fotos e quaisquer dados de pesquisa por rosto — a partir da Photohawk, e a Photohawk executará um pedido verificado por conta do organizador.",
          ],
        },
        {
          type: "p",
          text: "O encerramento de uma conta Photohawk cessa a faturação, mas não apaga por si só os dados armazenados; peça-nos o apagamento dos dados se for disso que necessita.",
        },
      ],
    },
    {
      heading: "11. Acordo de Tratamento de Dados (DPA)",
      blocks: [
        {
          type: "p",
          text: "Para os fotógrafos/organizadores, o nosso tratamento dos dados dos seus participantes é regido por um Acordo de Tratamento de Dados ao abrigo do art. 28 do GDPR, que disponibilizamos mediante pedido em support@photohawk.com.",
        },
      ],
    },
    {
      heading: "12. Cookies e tecnologias semelhantes",
      blocks: [
        {
          type: "ul",
          items: [
            "Os cookies estritamente necessários (para o início de sessão, a segurança e as funcionalidades essenciais) estão sempre ativos; não requerem consentimento.",
            "Os cookies de análise de audiência e publicitários — Google Analytics e o Meta Pixel (ver §7) — não são essenciais e apenas são carregados após o seu consentimento, dado através do nosso banner de consentimento de cookies. Pode alterar ou retirar a sua escolha a qualquer momento através do banner / das definições de cookies. A recusa deixa o serviço plenamente utilizável.",
            "Mais pormenores constam da nossa Política de Cookies separada.",
          ],
        },
      ],
    },
    {
      heading: "13. Dados de menores",
      blocks: [
        {
          type: "p",
          text: "Os eventos podem incluir menores. Decidir se a pesquisa por rosto é adequada a um evento cabe ao administrador da galeria (ver §4); quando possam estar presentes menores e a lei local exija o consentimento das pessoas fotografadas, o administrador tem de ter, para eles, o consentimento de um progenitor ou tutor, ou utilizar em vez disso a pesquisa por dorsal e por hora. A Photohawk não cria conscientemente dados biométricos de uma criança sem um consentimento exigido por lei, e o passo de consentimento integrado reforça este princípio.",
        },
      ],
    },
    {
      heading: "14. Violações de dados",
      blocks: [
        {
          type: "p",
          text: "Em caso de violação de dados pessoais, a Photohawk notificará o responsável pelo tratamento em causa sem demora injustificada após dela tomar conhecimento, e ajudá-lo-á a cumprir as suas próprias obrigações de notificação às autoridades de controlo e às pessoas em causa (Art. 33–34 do GDPR).",
        },
      ],
    },
    {
      heading: "15. Alterações a esta política",
      blocks: [
        {
          type: "p",
          text: "Podemos atualizar esta política; a «Data de efeito» acima indica a versão em vigor. As alterações substanciais serão comunicadas aos titulares de conta.",
        },
      ],
    },
    {
      heading: "16. Contacto",
      blocks: [
        {
          type: "p",
          text: "Encarregado da Proteção de Dados — Andrew Hammond. CLOUDCONCUR LTD (número de sociedade 13399913), que opera sob o nome Photohawk. E-mail: support@photohawk.com · 23 Caldey Gardens, Ingleby Barwick, Stockton-on-Tees, TS17 5HW, United Kingdom.",
        },
      ],
    },
  ],
};

export default gdprPt;
